import React from "react";
import * as io from "io-ts";
import createGlobalState from "@ui/lib/createGlobalState";
import { DEFAULT_PAGINATION_VALUE, PaginationQueryType } from "../../../../api/pagination";
import Filters, { useFilterValues } from "./Filters";
import Table from "@ui/Table";
import useMetadata from "./useMetadata";
import useDebounce from "@ui/lib/hooks/useDebounce";
import TableCard from "@ui/Table/Card";
import Button from "@ui/Button";
import { AddSelection, CancelSelection } from "@ui/Table/useSelectedRows";
import t from "../../../../i18n";
import InlineMenu from "@ui/InlineMenu";
import { styled } from "@linaria/react";
import { COLUMNS, requiredVisibleColumns, useColumns } from "./useColumns";
import useModal from "@ui/lib/hooks/useModal";
import Link from "@ui/Link";
import Option from "@ui/Option";
import { PageSizeSelector, PageSwitcher } from "@ui/Table/Pagination";
import Modal from "@ui/Modal";
import ModalForm from "./ModalForm";
import { useCanUserDo } from "../../../canUserDo";
import type { ClassificationFailure } from "../../../../api/useClassificationFailures";
import { useClassificationFailures } from "../../../../api/useClassificationFailures";
import type { Input } from "../../../../api/call";

const StyledInlineMenu = styled(InlineMenu)`
  font-size: 14px;
`;

const computeRowKey = (q: ClassificationFailure) => `failure-id-${q.id}`;

const useDisplayDraft = createGlobalState({
  storage: "session",
  key: "administrationFailureTableDisplayDraft",
  type: io.boolean,
  defaultValue: false,
  scoped: false,
});

const useDisplayManualFailures = createGlobalState({
  storage: "session",
  key: "displayManualFailures",
  type: io.boolean,
  defaultValue: false,
  scoped: false,
});

const usePagination = createGlobalState({
  storage: "session",
  key: "adminFailuresTablePagination",
  type: PaginationQueryType,
  defaultValue: DEFAULT_PAGINATION_VALUE,
  scoped: false,
});

const useSearch = createGlobalState({
  storage: "session",
  key: "administrationFailuresSearch",
  type: io.string,
  defaultValue: "",
  scoped: false,
});

const useSelectedFailures = createGlobalState({
  storage: "session",
  key: "selectedAdministrationFailures",
  type: io.array(io.string),
  defaultValue: [],
  scoped: false,
});

export const FailureClassification = () => {
  const userHasCategoryEdit = useCanUserDo("CATEGORY_EDIT");
  const [visibleColumns, setVisibleColumns] = useColumns({});
  const [displayManualFailures, setDisplayManualFailures] = useDisplayManualFailures({});
  const [displayDraft, setDisplayDraft] = useDisplayDraft({});
  const [pagination, setPagination] = usePagination({});
  const searchState = useSearch({});
  const [selectedFailures, setSelectedFailures] = useSelectedFailures({});

  const tableMetadata = useMetadata({ displayManualFailures });
  const [menuOpened, setMenuOpened] = React.useState(false);
  const { modal, openModal, closeModal } = useModal();
  const [selectAction, setSelectAction] = React.useState(selectedFailures.length > 0);
  const deferredSelectAction = React.useDeferredValue(selectAction);

  const handleDisplayDraftChange = React.useCallback(() => {
    setDisplayDraft(!displayDraft);
  }, [setDisplayDraft, displayDraft]);

  const handleDisplayManualFailuresChange = React.useCallback(() => {
    setDisplayManualFailures(!displayManualFailures);
    setSelectedFailures([]);
  }, [setDisplayManualFailures, displayManualFailures, setSelectedFailures]);

  const cancelSelection = React.useCallback(() => {
    setSelectedFailures([]);
    setSelectAction(false);
  }, [setSelectedFailures]);

  const resetPagination = React.useCallback(
    () => setPagination(currentPagination => ({ ...currentPagination, page: 1 })),
    [setPagination],
  );

  const [filterValues, setFilterValues] = useFilterValues({});

  const apiFilters = React.useMemo<Input<"GET", "/failures">["body"]>(
    () => ({
      name: searchState[0].length === 0 ? null : encodeURI(searchState[0]),
      project: {
        ids: filterValues.project,
        siteIds: filterValues.projectSite,
        typeIds: filterValues.projectType,
        includeDraft: displayDraft,
      },
      systemIds: filterValues.system,
      degradationTypeIds: filterValues.degradationType,
      reparabilities: filterValues.reparability,
      isManual: displayManualFailures,
    }),
    [
      filterValues.project,
      filterValues.projectType,
      filterValues.projectSite,
      filterValues.system,
      filterValues.reparability,
      filterValues.degradationType,
      searchState,
      displayDraft,
      displayManualFailures,
    ],
  );

  const apiParams = React.useMemo(
    () => ({
      query: pagination,
      body: apiFilters,
    }),
    [apiFilters, pagination],
  );

  const deferredApiParams = React.useDeferredValue(useDebounce(apiParams, 300));
  const dataOutdated = deferredApiParams !== apiParams;
  const { elements: failures, totalElements } = useClassificationFailures(deferredApiParams);

  const actionSection = React.useMemo(
    () => (
      <>
        {selectAction ? (
          <>
            <Button
              slim
              variant="primary"
              onClick={openModal}
              disabled={!selectedFailures.length}
              title={t("administration.table.failures.selection_button_title")}
            >
              {t("administration.failures.table.header.actions.update", selectedFailures.length)}
            </Button>
            <CancelSelection onClick={cancelSelection}>
              {t("projects.header.actions.cancel_selection")}
            </CancelSelection>
          </>
        ) : (
          <>
            <StyledInlineMenu
              anchor="right"
              opened={menuOpened}
              setOpened={setMenuOpened}
              caret
              overlay={COLUMNS.flatMap(column => {
                if (!(column in tableMetadata)) return [];
                return [
                  <Option
                    key={column}
                    checkbox
                    disabled={requiredVisibleColumns.includes(column)}
                    selected={visibleColumns.includes(column)}
                    onClick={() =>
                      setVisibleColumns(
                        visibleColumns.includes(column)
                          ? visibleColumns.filter(c => c !== column)
                          : [...visibleColumns, column],
                      )
                    }
                  >
                    {tableMetadata[column]?.label}
                  </Option>,
                ];
              })}
            >
              <Link>{t("commons.select.manage_columns")}</Link>
            </StyledInlineMenu>
            {userHasCategoryEdit && (
              <AddSelection
                slim
                onClick={() => setSelectAction(true)}
                title={t("administration.table.failures.selection_button_title")}
              >
                {t("administration.failures.table.header.actions.update", 0)}
              </AddSelection>
            )}
          </>
        )}
      </>
    ),
    [
      selectAction,
      openModal,
      selectedFailures.length,
      cancelSelection,
      menuOpened,
      userHasCategoryEdit,
      visibleColumns,
      tableMetadata,
      setVisibleColumns,
    ],
  );

  const pageChangedHandler = React.useCallback(
    (pageIndex: number) => {
      setPagination(state => ({ page: pageIndex + 1, pageSize: state.pageSize }));
    },
    [setPagination],
  );

  const pageSizeChangeHandler = React.useCallback(
    (size: number) => {
      setPagination({ page: 1, pageSize: size });
    },
    [setPagination],
  );

  const onRowsSelection = React.useCallback(
    (data: ClassificationFailure[]) => {
      if (data.length > 0) setSelectedFailures(data.map(({ id }) => id));
    },
    [setSelectedFailures],
  );

  const selectedDatas = React.useMemo(
    () => failures.filter(({ id }) => selectedFailures.includes(id)),
    [failures, selectedFailures],
  );

  return (
    <>
      <Modal
        title={t("administration.failures.table.header.actions.update", selectedFailures.length)}
        isOpen={modal}
        onRequestClose={closeModal}
        size="l"
      >
        <ModalForm
          selectedFailures={selectedDatas}
          failures={failures}
          onRequestClose={closeModal}
          displayManualFailures={displayManualFailures}
        />
      </Modal>
      <TableCard
        title={null}
        headerLeft={
          <Filters
            displayDraft={displayDraft}
            displayManualFailures={displayManualFailures}
            onDisplayDraftChange={handleDisplayDraftChange}
            onDisplayManualFailuresChange={handleDisplayManualFailuresChange}
            searchState={searchState}
            filterValues={filterValues}
            setFilterValues={setFilterValues}
            resetPagination={resetPagination}
          />
        }
        headerRight={actionSection}
        pagination={
          <>
            <PageSizeSelector
              currentSelectionValue={pagination.pageSize}
              onPageSizeChanged={pageSizeChangeHandler}
            />
            <PageSwitcher
              pageIndex={pagination.page - 1}
              pageSize={pagination.pageSize}
              elementNumber={totalElements}
              onPageChanged={pageChangedHandler}
            />
          </>
        }
      >
        <Table
          stickyHeader
          onRowsSelection={deferredSelectAction ? onRowsSelection : undefined}
          datas={failures}
          selectedDatas={selectedDatas}
          metadata={tableMetadata}
          computeRowKey={computeRowKey}
          loading={dataOutdated}
        />
      </TableCard>
    </>
  );
};
